/**
 * Attach-time validation for pasted audio links in the video composer.
 *
 * Users routinely paste links that can never yield audio — streaming-service
 * song pages (Deezer, Apple Music), AI-music-site share pages (Mureka,
 * MusicAI), Google results, or whole share blurbs with a
 * URL buried in promo text. Those used to attach silently and only fail
 * minutes later inside the analyze job ("URL returned a web page instead of
 * audio"), which users answered by retrying the same link. Classifying the
 * obvious page links here turns that dead end into instant, specific
 * feedback. Anything not recognized still flows through — the server-side
 * content-type check in proxyAudioToS3 remains the backstop.
 */

import {
  RETIRED_AUDIO_LINK_MESSAGE,
  retiredAudioLinkPlatform,
} from "@shared/platformAudioLinks";

/** Pull candidate http(s) URLs out of pasted share text. */
export function extractHttpUrls(raw: string): string[] {
  const matches = raw.match(/https?:\/\/[^\s"'<>]+/gi) || [];
  // Share blurbs often end the URL with punctuation ("...ID 复制打开" / "...!").
  return matches.map((m) => m.replace(/[),.;!?。，、）】"']+$/g, ""));
}

type PageHostRule = {
  test: (host: string) => boolean;
  label: string;
  /** Replaces the default "download it as an MP3" advice for this host. */
  message?: string;
};

const hostIs = (suffix: string) => (host: string) =>
  host === suffix || host.endsWith(`.${suffix}`);

// Song-page hosts observed failing in production analyze jobs (Jul 2026),
// plus the major streaming services with the same no-downloadable-audio
// shape. Bandcamp/Audiomack are NOT listed — the server's yt-dlp generic
// extractor handles those. YouTube, TikTok and SoundCloud are handled first
// by retiredAudioLinkPlatform (shared/platformAudioLinks.ts).
const PAGE_HOST_RULES: PageHostRule[] = [
  { test: hostIs("deezer.com"), label: "Deezer" },
  { test: (h) => h === "music.apple.com", label: "Apple Music" },
  { test: hostIs("tidal.com"), label: "TIDAL" },
  { test: hostIs("pandora.com"), label: "Pandora" },
  { test: (h) => /(^|\.)music\.amazon\./.test(h), label: "Amazon Music" },
  { test: hostIs("mureka.ai"), label: "Mureka" },
  { test: hostIs("musicai.ai"), label: "MusicAI" },
  { test: hostIs("openmusic.ai"), label: "OpenMusic" },
  { test: hostIs("musicful.ai"), label: "Musicful" },
  { test: hostIs("promodj.com"), label: "PromoDJ" },
  { test: hostIs("seatunes.com"), label: "SeaTunes" },
  // Recurring page hosts behind "URL returned a web page" / "Cannot access
  // this audio URL" analyze failures, Aug 15 – Sep 29 2026: ≥2 projects each
  // (Facebook: 1, listed with Instagram), zero successful imports from any.
  { test: hostIs("instagram.com"), label: "Instagram" },
  { test: (h) => hostIs("facebook.com")(h) || h === "fb.watch", label: "Facebook" },
  // Udio plays songs as DRM-protected HLS (Widevine/FairPlay), its stored
  // MP3s answer 403, and it turned off downloads on Oct 29 2025, so the
  // usual "download it as an MP3" advice can't be followed (checked Oct 2026).
  {
    test: hostIs("udio.com"),
    label: "Udio",
    message:
      "Udio links can't be imported: Udio plays songs as copy-protected " +
      "streams, not audio files. If you have the song as an MP3 or WAV, " +
      "upload it here.",
  },
  { test: hostIs("musicgpt.com"), label: "MusicGPT" },
  { test: hostIs("makebestmusic.com"), label: "MakeBestMusic" },
  { test: hostIs("musicgeneratorai.com"), label: "MusicGeneratorAI" },
  { test: hostIs("topmediai.com"), label: "TopMediai" },
  { test: hostIs("aisong.io"), label: "AISong" },
  { test: hostIs("bandlab.com"), label: "BandLab" },
  {
    test: (h) => hostIs("chatgpt.com")(h) || h === "chat.openai.com",
    label: "ChatGPT",
  },
];

// A link straight to a media file is never a page, whatever host serves it
// (a site's own CDN subdomain, a shared file) — let the server fetch it.
const DIRECT_MEDIA_PATH_RE =
  /\.(mp3|wav|m4a|aac|ogg|oga|opus|flac|aiff?|mp4|m4v|mov|webm)$/i;

function parsedHost(url: string): { host: string; path: string } | null {
  try {
    const parsed = new URL(url);
    return { host: parsed.hostname.toLowerCase(), path: parsed.pathname };
  } catch {
    return null;
  }
}

/**
 * An email address typed into the link box ("name@gmail.com") parses as
 * https://name@gmail.com — userinfo on a mail host — and used to go to the
 * analyze job as a "link" (43 failed jobs from 2 users, Aug–Sep 2026). No
 * song link carries a username or password, so treat these as not a link.
 */
export function isEmailAddressLink(url: string): boolean {
  try {
    const parsed = new URL(url);
    return parsed.username !== "" || parsed.password !== "";
  } catch {
    return false;
  }
}

/**
 * Returns a user-facing error when the URL is a known page link that cannot
 * yield audio, or null when the link should be allowed through.
 */
export function getUnsupportedAudioPageError(url: string): string | null {
  // YouTube, TikTok and SoundCloud imports were retired on Oct 30 2026. This
  // matcher also reads scheme-less input, so it runs before the URL parse.
  if (retiredAudioLinkPlatform(url)) return RETIRED_AUDIO_LINK_MESSAGE;

  const parsed = parsedHost(url);
  if (!parsed) return null;
  const { host, path } = parsed;

  // Our own site pages: the song is already in the user's library.
  if (
    host === "solmi.ai" ||
    host === "www.solmi.ai" ||
    host === "songmakerai.ai" ||
    host === "www.songmakerai.ai"
  ) {
    return (
      "That's a link to a Solmi page. Pick the song from My Songs in the " +
      "composer instead, or upload the audio file directly."
    );
  }

  // Google search results and share.google result links (drive.google.com
  // intentionally not blocked).
  if (host === "google.com" || host === "www.google.com" || host === "share.google") {
    return (
      "Google links open search results, not audio files. Paste a direct " +
      "MP3 link, or upload the file instead."
    );
  }

  if (DIRECT_MEDIA_PATH_RE.test(path)) return null;
  for (const rule of PAGE_HOST_RULES) {
    if (rule.test(host)) {
      return (
        rule.message ??
        `${rule.label} links open a web page, not an audio file. Download ` +
          "the song as an MP3 and upload it here."
      );
    }
  }

  return null;
}
